import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  dashboardTicketsOptions,
  dashboardTicketsPageQuery,
  dashboardTicketsSubcategories,
  dashboardTicketsTableQuery,
  saveDashboardTickets,
  type CheckedOption,
  type DashboardOptions,
  type NamedOption,
} from '../../api/settingsEaseedeskDashboardTickets'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Dashboard tickets settings.
 *
 * Umple cate o casuta din tabloul de tichete: pe orizontala CENTRELE
 * (HelpDesk, Field, National, Monitoring...), pe verticala RANDURILE (Open
 * Today, Unassigned, In Progress, Connected, Resolved Today...).
 *
 * DOUA LUCRURI SUNT ALTFEL DECAT IN `dashboard_tickets/index.php`:
 *
 * 1. CE BLOCURI SE ARATA vine de la server. In macheta veche, JavaScript-ul
 *    avea numerele scrise de mana - `if (serviceProvider == 59) { val1 = 73;
 *    val2 = 74; cat1 = 10 } else { 57; 58; 8 }`, plus o lista de douazeci de
 *    numere pentru randurile cu stari. Trei fisiere tineau trei liste care nu
 *    se potriveau intre ele, si una din ele nu se lua niciodata. Acum serverul
 *    se uita la NUMELE randului, cum face si tabloul cand citeste.
 *
 * 2. LISTA DE JOS ARE UN RAND PE SETARE. `getDashboartTicketsList()` scotea din
 *    fiecare setare cate un rand pentru FIECARE tip, FIECARE stare si FIECARE
 *    echipa - toate cu acelasi id - dar numara doar setarile: pagina a doua
 *    sarea peste jumatate din ce se vedea pe prima.
 */

const PAGE = 25

function CheckList({
  title,
  rows,
  chosen,
  onChange,
}: {
  title: string
  rows: CheckedOption[]
  chosen: number[]
  onChange(next: number[]): void
}) {
  return (
    <div className="form-group">
      <label className="control-label">{title}</label>
      {rows.length === 0 ? (
        <span className="help-block">- no results available -</span>
      ) : (
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: 8 }}>
          {rows.map((row) => (
            <div className="checkbox" key={row.ID} style={{ margin: '2px 0' }}>
              <label>
                <input
                  type="checkbox"
                  checked={chosen.includes(row.ID)}
                  onChange={(e) =>
                    onChange(e.target.checked ? [...chosen, row.ID] : chosen.filter((one) => one !== row.ID))
                  }
                />{' '}
                {row.name}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SettingsModal({
  categories,
  open,
  onClose,
  onSaved,
}: {
  categories: NamedOption[]
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const [category, setCategory] = useState(0)
  const [subcategory, setSubcategory] = useState(0)
  const [subcategories, setSubcategories] = useState<NamedOption[]>([])
  const [options, setOptions] = useState<DashboardOptions | null>(null)
  const [types, setTypes] = useState<number[]>([])
  const [statuses, setStatuses] = useState<number[]>([])
  const [groups, setGroups] = useState<number[]>([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) return
    setCategory(0)
    setSubcategory(0)
    setSubcategories([])
    setOptions(null)
  }, [open])

  const chooseCategory = async (next: number) => {
    setCategory(next)
    setSubcategory(0)
    setOptions(null)
    setSubcategories([])
    if (next < 1) return
    setBusy(true)
    try {
      setSubcategories((await dashboardTicketsSubcategories(next)).subcategories)
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const chooseSubcategory = async (next: number) => {
    setSubcategory(next)
    setOptions(null)
    if (next < 1) return
    setBusy(true)
    try {
      const answer = await dashboardTicketsOptions(category, next)
      setOptions(answer)
      setTypes(answer.ticket_types.filter((one) => one.checked).map((one) => one.ID))
      setStatuses(answer.statuses.filter((one) => one.checked).map((one) => one.ID))
      setGroups(answer.groups.filter((one) => one.checked).map((one) => one.ID))
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const save = useMutation({
    mutationFn: () =>
      saveDashboardTickets({
        category,
        subcategory,
        selected_tickets: options?.shows_ticket_types === true ? types : [],
        status: options?.shows_statuses === true ? statuses : [],
        group: options?.shows_groups === true ? groups : [],
      }),
    onSuccess: () => {
      toast('Dashboard settings saved.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="ModalSettingsDashboard"
      labelledBy="newSettingsModalLabel"
      open={open}
      onClose={onClose}
      title="New settings"
    >
      <form
        id="save-settings-dashboard-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (category < 1 || subcategory < 1) return
          save.mutate()
        }}
      >
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label">Dashboard Category</label>
              <select
                className="form-control"
                id="category"
                name="category"
                value={category || ''}
                onChange={(e) => void chooseCategory(Number(e.target.value))}
              >
                <option value="">- select -</option>
                {categories.map((one) => (
                  <option key={one.ID} value={one.ID}>
                    {one.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="control-label">Dashboard Subcategory</label>
              <select
                className="form-control"
                id="subcategory"
                name="subcategory"
                value={subcategory || ''}
                onChange={(e) => void chooseSubcategory(Number(e.target.value))}
              >
                <option value="">{subcategories.length === 0 ? '- no results available -' : '- select -'}</option>
                {subcategories.map((one) => (
                  <option key={one.ID} value={one.ID}>
                    {one.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {busy && <Spinner />}

        {options !== null && (
          <>
            {options.shows_ticket_types && (
              <CheckList title="Ticket types" rows={options.ticket_types} chosen={types} onChange={setTypes} />
            )}
            {options.shows_statuses && (
              <CheckList title="Statuses" rows={options.statuses} chosen={statuses} onChange={setStatuses} />
            )}
            {options.shows_groups && (
              <CheckList
                title={options.group_role === 'managers' ? 'Managers' : 'Technicians'}
                rows={options.groups}
                chosen={groups}
                onChange={setGroups}
              />
            )}
          </>
        )}

        <hr className="dotted" />
        <div style={{ textAlign: 'right' }}>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-success"
            disabled={save.isPending || options === null}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function DashboardTicketsPage() {
  const queryClient = useQueryClient()
  const page = useQuery(dashboardTicketsPageQuery())
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [adding, setAdding] = useState(false)

  const table = useQuery(dashboardTicketsTableQuery(search, start, PAGE))

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'dashboard_tickets'] })
  }

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <LoadFailed what="This screen" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  const rows = table.data?.data ?? []
  const total = table.data?.recordsTotal ?? 0

  return (
    <div>
      <h4>
        <strong>Dashboard Tickets</strong> Settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>general settings</strong> for Dashboard Tickets
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setAdding(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create Settings
      </button>
      <hr />

      <div className="form-group" style={{ maxWidth: 320 }}>
        <input
          type="search"
          className="form-control input-sm"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setStart(0)
            setSearch(e.target.value)
          }}
        />
      </div>

      {table.error ? (
        <LoadFailed what="The settings list" error={table.error} onRetry={() => void table.refetch()} />
      ) : !table.data ? (
        <Spinner />
      ) : (
        <table className="table table-bordered dataTable" width="100%">
          <thead>
            <tr>
              <th className="a-left">Category</th>
              <th className="a-left">Subcategory</th>
              <th className="a-left">Ticket Types</th>
              <th className="a-left">Status</th>
              <th className="a-left">Group</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center' }}>
                  Nothing to see yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.ID} id={`row_${row.ID}`}>
                  <td>{row.category ?? ''}</td>
                  <td>{row.subcategory ?? ''}</td>
                  <td>{row.ticket_types ?? ''}</td>
                  <td>{row.ticket_statuses ?? ''}</td>
                  <td>{row.technicians ?? row.managers ?? ''}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {total > PAGE && (
        <div className="aright">
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start === 0}
            onClick={() => setStart(Math.max(0, start - PAGE))}
          >
            Previous
          </button>{' '}
          <span>
            {start + 1}-{Math.min(start + PAGE, total)} of {total}
          </span>{' '}
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start + PAGE >= total}
            onClick={() => setStart(start + PAGE)}
          >
            Next
          </button>
        </div>
      )}

      <SettingsModal
        categories={page.data.categories}
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={reload}
      />
    </div>
  )
}
